"use client";

import type { AdminAccessPolicy } from "@/lib/auth/admin-access";
import type { SessionData } from "@/lib/types/auth";
import {
  ChevronDown,
  Globe,
  KeyRound,
  LogOut,
  Pencil,
  User,
} from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils/utils";

interface HeaderProps {
  session: SessionData | null;
  adminAccess: AdminAccessPolicy | null;
}

export function Header({ session, adminAccess }: HeaderProps) {
  const t = useTranslations();
  const pathname = usePathname();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const langMenuRef = useRef<HTMLDivElement>(null);
  const canAccessAdministration = adminAccess?.hasAccess ?? false;
  const adminHref = adminAccess?.isAdmin
    ? "/admin#users"
    : "/admin#user-access";

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setIsUserMenuOpen(false);
      }
      if (
        langMenuRef.current &&
        !langMenuRef.current.contains(event.target as Node)
      ) {
        setIsLangMenuOpen(false);
      }
    };

    if (isUserMenuOpen || isLangMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isUserMenuOpen, isLangMenuOpen]);

  const handleSignOut = async () => {
    await fetch("/auth/logout", { method: "POST", redirect: "manual" });
    window.location.href = "/login";
  };

  const handleLocaleChange = async (locale: string) => {
    // Set the cookie immediately for faster perceived change
    document.cookie = `NEXT_LOCALE=${locale}; path=/; max-age=31536000`;

    // Update Zitadel user profile if logged in
    if (session?.user) {
      try {
        await fetch("/api/user/locale", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ locale }),
        });
      } catch {
        // Continue with page reload even if Zitadel update fails
        // The cookie will still ensure the UI shows the correct language
      }
    }

    window.location.reload();
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-card/95 backdrop-blur supports-backdrop-filter:bg-card/60">
      <div className="container mx-auto flex h-16 items-center justify-between px-4 lg:px-8">
        {/* Logo */}
        <div className="flex items-center gap-8">
          <div className="relative h-8 w-40">
            {/* next/image injects inline style= attributes that violate strict CSP */}
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/exawizards-logo-light.svg"
              alt="ExaWizards"
              className="h-full w-full object-contain"
            />
          </div>

          {/* Navigation Links */}
          {session?.user && (
            <nav className="hidden md:flex items-center gap-6">
              <Link
                href="/"
                className={cn(
                  "text-sm font-medium transition-colors hover:text-primary",
                  pathname === "/" ? "text-primary" : "text-muted-foreground",
                )}
              >
                {t("nav.products")}
              </Link>
              {canAccessAdministration && (
                <Link
                  href={adminHref}
                  className={cn(
                    "text-sm font-medium transition-colors hover:text-primary",
                    pathname?.startsWith("/admin")
                      ? "text-primary"
                      : "text-muted-foreground",
                  )}
                >
                  {t("nav.administration")}
                </Link>
              )}
              {session.user.isCustomerManager && (
                <Link
                  href="/customer-management"
                  className={cn(
                    "text-sm font-medium transition-colors hover:text-primary",
                    pathname?.startsWith("/customer-management")
                      ? "text-primary"
                      : "text-muted-foreground",
                  )}
                >
                  {t("nav.customerManagement")}
                </Link>
              )}
              {session.user.isProjectManager && (
                <Link
                  href="/project-management"
                  className={cn(
                    "text-sm font-medium transition-colors hover:text-primary",
                    pathname?.startsWith("/project-management")
                      ? "text-primary"
                      : "text-muted-foreground",
                  )}
                >
                  {t("nav.projectManagement")}
                </Link>
              )}
            </nav>
          )}
        </div>

        {/* Right side - Language switcher and User menu */}
        <div className="flex items-center gap-4">
          {/* Language Switcher */}
          <div className="relative" ref={langMenuRef}>
            <button
              onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
              className="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted"
              aria-label={t("nav.language")}
            >
              <Globe size={18} />
              <ChevronDown
                size={16}
                className={cn(
                  "transition-transform",
                  isLangMenuOpen && "rotate-180",
                )}
              />
            </button>

            {isLangMenuOpen && (
              <div className="absolute right-0 mt-2 w-40 rounded-lg border border-border bg-card shadow-lg">
                <button
                  onClick={() => handleLocaleChange("en")}
                  className="w-full cursor-pointer px-4 py-2 text-left text-sm hover:bg-muted"
                >
                  English
                </button>
                <button
                  onClick={() => handleLocaleChange("ja")}
                  className="w-full cursor-pointer px-4 py-2 text-left text-sm hover:bg-muted"
                >
                  日本語
                </button>
              </div>
            )}
          </div>

          {/* User Menu */}
          {session?.user ? (
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="flex cursor-pointer items-center gap-3 rounded-lg border border-border bg-muted/50 px-4 py-2 transition-colors hover:bg-muted"
              >
                <div className="relative flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary text-white">
                  {session.user.image ? (
                    // next/image injects inline style= attributes that violate strict CSP
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={session.user.image}
                      alt=""
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                  ) : (
                    <User size={18} />
                  )}
                </div>
                <div className="hidden text-left md:block">
                  <p className="text-sm font-medium text-foreground">
                    {session.user.name ||
                      session.user.preferredUsername ||
                      session.user.email}
                  </p>
                  {session.user.orgName && (
                    <p className="text-xs text-muted-foreground">
                      {session.user.orgName}
                    </p>
                  )}
                </div>
                <ChevronDown
                  size={16}
                  className={cn(
                    "transition-transform",
                    isUserMenuOpen && "rotate-180",
                  )}
                />
              </button>

              {isUserMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 rounded-lg border border-border bg-card shadow-lg">
                  <div className="border-b border-border px-4 py-3">
                    <p className="text-sm font-medium text-foreground">
                      {session.user.name || "User"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {session.user.preferredUsername || session.user.email}
                    </p>
                  </div>
                  <Link
                    href="/profile"
                    onClick={() => setIsUserMenuOpen(false)}
                    className="flex w-full items-center gap-2 px-4 py-2 text-sm text-foreground hover:bg-muted"
                  >
                    <Pencil size={16} />
                    {t("nav.editProfile")}
                  </Link>
                  <Link
                    href="/profile/edit-password"
                    onClick={() => setIsUserMenuOpen(false)}
                    className="flex w-full items-center gap-2 px-4 py-2 text-sm text-foreground hover:bg-muted"
                  >
                    <KeyRound size={16} />
                    {t("nav.editPassword")}
                  </Link>
                  <button
                    onClick={handleSignOut}
                    className="flex w-full cursor-pointer items-center gap-2 px-4 py-2 text-sm text-error hover:bg-muted"
                  >
                    <LogOut size={16} />
                    {t("auth.signOut")}
                  </button>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}
